import { useEffect } from 'react'

import { Button, ButtonVariants } from '@inno-ui/button'
import { toast } from '@inno-ui/toast'
import { SavedUser } from 'modules/auth/components'
import { useCurrentSelectableUser } from 'modules/auth/hooks/useCurrentSelectableUser'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  usePasswordRecoveryCheckMutation,
  usePasswordRecoveryMutation
} from 'shared/api/auth/requests'
import cyanOrangeBg from 'shared/assets/wallpapers/cyan-orange.webp'
import { APP_ROUTES } from 'shared/constants/routes'
import { useSearchParams } from 'shared/hooks/useSearchParams'

import { AuthPageTemplate } from '../../templates'
import styles from './ForgotPasswordPage.module.scss'
import { RequestEmailForm, SetNewPasswordForm } from './components'

export const ForgotPasswordPage = () => {
  const { t } = useTranslation('auth', {
    keyPrefix: 'forgot_password'
  })
  const navigate = useNavigate()
  const selectedUser = useCurrentSelectableUser()

  const onForgotPasswordSuccess = () => {
    toast.success({
      title: t('success.title'),
      subTitle: t('success.description')
    })
    navigate(APP_ROUTES.login.path)
  }

  const passwordRecoveryCheckMutation = usePasswordRecoveryCheckMutation({
    onError: () => {
      navigate(APP_ROUTES.desktop.path)
    }
  })
  const passwordRecoveryMutation = usePasswordRecoveryMutation({
    onSuccess: onForgotPasswordSuccess
  })

  const [{ token }] = useSearchParams<{ token?: string }>()

  useEffect(() => {
    if (token && passwordRecoveryCheckMutation.isIdle) {
      passwordRecoveryCheckMutation.mutate({ token })
    }
  }, [token, passwordRecoveryCheckMutation])

  return (
    <AuthPageTemplate backgroundImageUrl={cyanOrangeBg} backButton>
      <div className={styles.forgotPasswordPage}>
        <div className={styles.content}>
          <div className={styles.header}>
            {!selectedUser ? (
              <h1 className={styles.title}>{t('title')}</h1>
            ) : (
              <SavedUser user={selectedUser} size='big' />
            )}
            <p className={styles.description}>
              {token
                ? t('desc_set_password')
                : selectedUser
                  ? t('desc_selected_user')
                  : t('desc_new_login')}
            </p>
          </div>
          {token ? (
            passwordRecoveryCheckMutation.isSuccess ? (
              <SetNewPasswordForm token={token} />
            ) : null
          ) : selectedUser ? (
            <Button
              label={t('request_password')}
              onClick={() => {
                passwordRecoveryMutation.mutate({
                  email: selectedUser.email,
                  application: 'innos'
                })
              }}
              variant={ButtonVariants.Dark}
              isLoading={passwordRecoveryMutation.isPending}
            />
          ) : (
            <RequestEmailForm onSuccess={onForgotPasswordSuccess} />
          )}
        </div>
      </div>
    </AuthPageTemplate>
  )
}
